/**
 * Vocabulario de la bandeja: severidad, estado del caso y motivo de cierre.
 *
 * El color es estado y nada más. `Badge` tiene cuatro tonos y no hay un
 * quinto, así que la severidad se reparte entre rojo (hay que actuar ya),
 * ámbar (sospecha, hay que mirarlo) y neutro (ruido de fondo). La palabra
 * —crítica, alta, media— es la que distingue los cinco niveles; el color sólo
 * dice si alguien tiene que levantarse de la silla.
 */
import type { MessageKey } from '@/i18n/messages';
import type { Tone } from '@/ui/Badge';

/** `AlertSeverity` de `services/shared/models.py`. Mayor es más grave. */
export type Severity = 1 | 2 | 3 | 4 | 5;
export type AlertStatus = 'open' | 'investigating' | 'confirmed' | 'dismissed';

/** De la más grave a la menos: es el orden de atención de la bandeja. */
export const SEVERITIES: readonly Severity[] = [5, 4, 3, 2, 1];

const SEVERITY_LABELS: Record<Severity, MessageKey> = {
  5: 'alerts.severity.critical',
  4: 'alerts.severity.high',
  3: 'alerts.severity.medium',
  2: 'alerts.severity.low',
  1: 'alerts.severity.info',
};

const SEVERITY_TONES: Record<Severity, Tone> = {
  5: 'critical',
  4: 'critical',
  3: 'suspect',
  2: 'suspect',
  1: 'neutral',
};

function asSeverity(value: number): Severity | null {
  return value === 1 || value === 2 || value === 3 || value === 4 || value === 5 ? value : null;
}

export function severityLabelKey(value: number): MessageKey | null {
  const severity = asSeverity(value);
  return severity === null ? null : SEVERITY_LABELS[severity];
}

export function severityTone(value: number): Tone {
  const severity = asSeverity(value);
  return severity === null ? 'neutral' : SEVERITY_TONES[severity];
}

// ─────────────────────────────────────────────────── estado del caso

export const CASE_STATUSES: readonly AlertStatus[] = [
  'open',
  'investigating',
  'confirmed',
  'dismissed',
];

const STATUS_LABELS: Record<AlertStatus, MessageKey> = {
  open: 'alerts.status.open',
  investigating: 'alerts.status.investigating',
  confirmed: 'alerts.status.confirmed',
  dismissed: 'alerts.status.dismissed',
};

const STATUS_TONES: Record<AlertStatus, Tone> = {
  open: 'suspect',
  investigating: 'suspect',
  // Confirmado = falsificación, desvío o robo probado. Es rojo porque a
  // partir de acá hay una acción legal o sanitaria detrás.
  confirmed: 'critical',
  dismissed: 'neutral',
};

export function statusLabelKey(status: string): MessageKey | null {
  return isCaseStatus(status) ? STATUS_LABELS[status] : null;
}

export function statusTone(status: string): Tone {
  return isCaseStatus(status) ? STATUS_TONES[status] : 'neutral';
}

export function isCaseStatus(value: string): value is AlertStatus {
  return (CASE_STATUSES as readonly string[]).includes(value);
}

/**
 * Transiciones legítimas. Espejo de `_TRANSITIONS` en
 * `services/api/routers/cases.py`: la autoridad es la API, esto evita ofrecer
 * un botón que sólo puede terminar en 409.
 */
const TRANSITIONS: Record<AlertStatus, readonly AlertStatus[]> = {
  open: ['investigating', 'confirmed', 'dismissed'],
  investigating: ['confirmed', 'dismissed', 'open'],
  // Un caso cerrado se reabre; lo que no se puede es saltar de confirmado a
  // descartado sin volver a abrirlo, porque eso borra por qué se confirmó.
  confirmed: ['open'],
  dismissed: ['open'],
};

export function allowedTransitions(from: string): readonly AlertStatus[] {
  return isCaseStatus(from) ? TRANSITIONS[from] : [];
}

/** Confirmar y descartar exigen motivo (`_NEEDS_RESOLUTION`). */
export function needsResolution(status: AlertStatus): boolean {
  return status === 'confirmed' || status === 'dismissed';
}

// ──────────────────────────────────────────────────── motivo de cierre

/** Espejo del CHECK `alert_resolution_ck`. */
export const RESOLUTIONS = [
  'counterfeit_confirmed',
  'diversion_confirmed',
  'theft_confirmed',
  'false_positive',
  'consumer_error',
  'test_scan',
  'no_action',
] as const;

export type Resolution = (typeof RESOLUTIONS)[number];

const RESOLUTION_LABELS: Record<Resolution, MessageKey> = {
  counterfeit_confirmed: 'alerts.resolution.counterfeit',
  diversion_confirmed: 'alerts.resolution.diversion',
  theft_confirmed: 'alerts.resolution.theft',
  false_positive: 'alerts.resolution.falsePositive',
  consumer_error: 'alerts.resolution.consumerError',
  test_scan: 'alerts.resolution.testScan',
  no_action: 'alerts.resolution.noAction',
};

export function resolutionLabelKey(resolution: string): MessageKey | null {
  return (RESOLUTIONS as readonly string[]).includes(resolution)
    ? RESOLUTION_LABELS[resolution as Resolution]
    : null;
}

// ────────────────────────────────────────────────────── tipo de regla

/**
 * Las once reglas del motor de anomalías. La lista NO es exhaustiva por
 * contrato: las reglas viven en un registry (`services/worker/rules/`) y
 * agregar una no puede obligar a tocar el dashboard. Un tipo desconocido se
 * muestra con su nombre crudo, que es información igual.
 */
const TYPE_LABELS: Record<string, MessageKey> = {
  not_activated: 'alerts.type.notActivated',
  stolen_roll: 'alerts.type.stolenRoll',
  recalled: 'alerts.type.recalled',
  secret_reuse: 'alerts.type.secretReuse',
  impossible_travel: 'alerts.type.impossibleTravel',
  territory_diversion: 'alerts.type.territoryDiversion',
  geo_cluster: 'alerts.type.geoCluster',
  duplicate_scan: 'alerts.type.duplicateScan',
  velocity_spike: 'alerts.type.velocitySpike',
  low_device_diversity: 'alerts.type.lowDeviceDiversity',
  datacenter_asn: 'alerts.type.datacenterAsn',
};

export function alertTypeLabelKey(alertType: string): MessageKey | null {
  return TYPE_LABELS[alertType] ?? null;
}
